import React from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import './EquityChart.css';

/** The shared account-value/benchmark renderer. Callers own data and units, not plot spacing. */
export default function EquityChart({
  data,
  height = 336,
  valueKey,
  benchmarkKey = undefined,
  color,
  domain,
  xAxis,
  yAxis = {},
  minimal = false,
  areaBase = undefined,
  formatEndpoint,
  showArea = true,
  initialDimension,
  children,
}) {
  const [mobile, setMobile] = React.useState(() => window.matchMedia('(max-width: 600px)').matches);
  React.useEffect(() => {
    const media = window.matchMedia('(max-width: 600px)');
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const points = data.filter((point) => Number.isFinite(point[valueKey]));
  // A hidden comparison must not extend the date axis past the visible history.
  // Keep every observation when the comparison is actually displayed.
  const plottedData = benchmarkKey ? data : points;
  const endpoints = points.length > 1 ? [points[0], points[points.length - 1]] : points;
  return (
    <div className="equity-chart" data-testid="shared-equity-chart">
      <ResponsiveContainer
        width="100%"
        height={height}
        initialDimension={initialDimension}
        minWidth={0}
      >
        <ComposedChart
          data={plottedData}
          margin={{ top: 30, right: 6, left: 6, bottom: mobile || minimal ? 8 : 20 }}
        >
          <CartesianGrid
            stroke="var(--theme-border, var(--backtest-border, #333))"
            strokeDasharray="3 5"
            vertical={false}
          />
          <XAxis {...xAxis} hide={minimal || mobile} padding={{ left: 0, right: 0 }} />
          {/* Values belong in the summary, endpoints and tooltip. Reserving an
              axis gutter wastes the same space in a narrow desktop panel as
              on a phone, so the shared plot never reserves that gutter. */}
          <YAxis {...yAxis} domain={domain} width={0} hide />
          {showArea && (
            <Area
              type="linear"
              dataKey={valueKey}
              baseValue={areaBase}
              stroke="none"
              fill={color}
              fillOpacity={0.1}
              legendType="none"
              tooltipType="none"
              connectNulls
              isAnimationActive={false}
            />
          )}
          <Line
            type="linear"
            dataKey={valueKey}
            name="Account Value"
            stroke={color}
            strokeWidth={2.5}
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
          {benchmarkKey && (
            <Line
              type="linear"
              dataKey={benchmarkKey}
              name="Benchmark"
              stroke="var(--theme-muted, var(--backtest-muted, #999))"
              strokeWidth={1.5}
              strokeDasharray="5 5"
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          )}
          {formatEndpoint &&
            endpoints.map((point, index) => (
              <ReferenceDot
                key={`endpoint-${index}`}
                x={point[xAxis.dataKey]}
                y={point[valueKey]}
                r={4}
                fill={color}
                stroke="var(--theme-surface, var(--backtest-surface, #111823))"
                strokeWidth={2}
                label={({ viewBox }) => (
                  <text
                    className="recharts-label"
                    x={viewBox.x + viewBox.width / 2}
                    y={viewBox.y - 8}
                    textAnchor={index === 0 ? 'start' : 'end'}
                    fill={color}
                    fontSize={11}
                    fontWeight={700}
                  >
                    {formatEndpoint(point[valueKey])}
                  </text>
                )}
              />
            ))}
          {children}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
